import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateWebsiteDesignFeedbackFormTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const ratingId = uuidv4();
  const ratingBlockId = uuidv4();
  const longText1Id = uuidv4();
  const longText1BlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const longText2Id = uuidv4();
  const longText2BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const mc1Option1Id = uuidv4();
  const mc1Option2Id = uuidv4();
  const mc1Option3Id = uuidv4();
  const mc1Option4Id = uuidv4();
  const mc2OptionYesId = uuidv4();
  const mc2OptionNoId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const ratingMediaId = uuidv4();
  const multipleChoice1MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.espressoGoldenSand,
    "collectionId": collectionId,
    "name": "Website Design Feedback form",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "How's your new site shaping up?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your thoughts help us refine our process and deliver even better results.",
        "thirdContent": "Give feedback",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": 50,
        "media": {
          "provider": "PEXEL",
          "id": welcomeMediaId,
          "type": "video",
          "name": "18069235",
          "link": "https://videos.pexels.com/video-files/18069235/18069235-hd_1280_720_24fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/18069235/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "First things first, what's your full name?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll personalize the rest of your journey.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `And your email, {{${contactInfoBlockId}__first_name}}?`,
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll reach out if we need any follow-ups.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "RATING",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": ratingId,
        "blockId": ratingBlockId,
        "content": "How would you rate the overall design of your website?",
        "manditory": true,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 5,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Think layout, feel, and first impressions.",
        "thirdContent": null,
        "forthContent": null,
        "icon": "star",
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": ratingMediaId,
          "type": "image",
          "name": "a bunch of different shapes and sizes of objects",
          "link": "https://unsplash.com/photos/hfQS1m9gBkE/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4Mnx8ZnV0dXJpc3RpYyUyMHNoYXBlc3xlbnwwfHx8fDE3NjQ1NDE3MTR8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Shubham Dhage",
          "authorProfile": "https://unsplash.com/@theshubhamdhage",
          "downloadLink": "https://api.unsplash.com/photos/hfQS1m9gBkE/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4Mnx8ZnV0dXJpc3RpYyUyMHNoYXBlc3xlbnwwfHx8fDE3NjQ1NDE3MTR8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText1Id,
        "blockId": longText1BlockId,
        "content": `What influenced that  {{${ratingBlockId}}}/5?`,
        "manditory": true,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'd love to hear what stood out, good or bad.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "What part of the final site are you happiest with?",
        "manditory": true,
        "order": 5,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "The visual identity & look"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "How easy everything is to navigate"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "How the content communicates your message"
          },
          {
            "id": mc1Option4Id,
            "order": 3,
            "value": "The overall polish & experience"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice1MediaId,
          "type": "image",
          "name": "a group of white eggs",
          "link": "https://unsplash.com/photos/_3yUyAOg_ZY/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxMHx8ZnV0dXJpc3RpYyUyMHNoYXBlcyUyMHdoaXRlfGVufDB8fHx8MTc2NDU0MTczMnww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "D koi",
          "authorProfile": "https://unsplash.com/@dkoi",
          "downloadLink": "https://api.unsplash.com/photos/_3yUyAOg_ZY/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxMHx8ZnV0dXJpc3RpYyUyMHNoYXBlcyUyMHdoaXRlfGVufDB8fHx8MTc2NDU0MTczMnww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "How likely are you to recommend us for web projects?",
        "manditory": true,
        "order": 6,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 0,
        "maxRange": 10,
        "minLabel": "Not likely at all",
        "maxLabel": "Extremely likely",
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "Did we deliver everything on schedule?",
        "manditory": false,
        "order": 7,
        "selectOptions": [
          {
            "id": mc2OptionYesId,
            "order": 0,
            "value": "Yes"
          },
          {
            "id": mc2OptionNoId,
            "order": 1,
            "value": "No"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText2Id,
        "blockId": longText2BlockId,
        "content": `Anything else you'd like to share,  {{${contactInfoBlockId}__first_name}}?`,
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Any extra details, suggestions, or praise are welcome.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "Thanks for the feedback!",
        "manditory": false,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We appreciate your time.",
        "thirdContent": "Visit Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": false,
        "opacity": 50,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "18069237",
          "link": "https://videos.pexels.com/video-files/18069237/18069237-uhd_1440_1440_24fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/18069237/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      }
    ],
    "snippets": [],
    "metaTitle": "Website Design Feedback form",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateWebsiteDesignFeedbackFormTemplate;